/* global React, window */
const { useState, useEffect, useMemo } = React;

// Painel "Histórico" da lista (menu lateral → Histórico): log de alterações
// e versões salvas. Os dados vêm do backend (WizardProgressHistoryService):
// o log é gerado lá a cada autosave aceito, então aparece aqui o que
// qualquer conta da escola mudou, não só esta aba.
//
// Sem `{ a, ...rest } = obj` neste arquivo: o Babel standalone compila o
// rest com um helper global que outro arquivo sobrescreve (ver o comentário
// do kit em steps-papelaria.jsx).

const HISTORY_FIELD_LABELS = {
  schoolName: "Nome da escola",
  schoolAccent: "Cor da escola",
  calendar: "Calendário",
  bimestres: "Distribuição por bimestre",
  pastListChoice: "Lista do ano anterior",
  pastListFiles: "Arquivos da lista anterior",
  ptFiles: "Arquivos (português)",
  enFiles: "Arquivos (inglês)",
  themes: "Temas",
  customThemes: "Temas personalizados",
  scolexPT: "Scolex (português)",
  scolexEN: "Scolex (inglês)",
  extraLangs: "Idiomas extras",
  qtyPerYear: "Livros por ano",
  qtyPerYearPT: "Livros por ano (português)",
  qtyPerYearEN: "Livros por ano (inglês)",
  qtyPerYearExtras: "Livros por ano (outros idiomas)",
  didaticosMode: "Modo dos didáticos",
  didaticosLangs: "Idiomas dos didáticos",
  papelariaMode: "Modo da papelaria",
  complementos: "Complementos",
  enabledSegments: "Segmentos",
  segmentLabels: "Nomes dos segmentos",
  enrolled: "Alunos por série",
  studentsBySeries: "Alunos por série",
};

const KIT_LABELS = { escola: "Kit escola", mochila: "Kit mochila" };

function fmtDateTime(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtTime(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function fmtDay(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function actorLabel(actor) {
  if (!actor) return "Sistema";
  return actor.name || actor.email || "Alguém da escola";
}

// Título do item: o backend já manda quando sabe (livros/itens buscados ao
// vivo); os do catálogo estático só existem aqui no front.
function historyItemTitle(c) {
  if (c.title) return c.title;
  if (c.type === "book" || c.type === "optional") {
    const book = (window.CATALOG || []).find((b) => b && b.id === c.itemId);
    if (book && book.title) return book.title;
  }
  if (c.type === "didatico" && typeof resolveDidatico === "function") {
    const d = resolveDidatico(c.itemId);
    if (d && d.title) return d.title;
  }
  if (c.type === "papelaria") {
    const it = (window.PAPELARIA_CATALOG || []).find((x) => x && x.id === c.itemId);
    if (it && (it.genericName || it.name)) return it.genericName || it.name;
  }
  return c.itemId || "item";
}

function historySeriesLabel(c) {
  if (c.series) return c.series;
  if (typeof c.seriesIdx === "number") return (window.SERIES_LIST || [])[c.seriesIdx] || `Série ${c.seriesIdx + 1}`;
  return null;
}

function fmtValue(v) {
  if (v == null || v === "") return "vazio";
  if (typeof v === "object" && v.omitted) return null;
  if (Array.isArray(v)) return v.length ? v.join(", ") : "vazio";
  if (typeof v === "object") return null;
  if (v === true) return "sim";
  if (v === false) return "não";
  return String(v);
}

function fmtPap(v) {
  if (!v) return "";
  const parts = [`${v.qty}×`];
  if (v.kit) parts.push(KIT_LABELS[v.kit] || v.kit);
  return parts.join(", ");
}

function describeChange(c) {
  const t = `“${historyItemTitle(c)}”`;
  switch (c.type) {
    case "series":
      if (c.op === "added") return { sign: "+", text: `Série “${historySeriesLabel(c)}” adicionada` };
      if (c.op === "removed") return { sign: "−", text: `Série “${historySeriesLabel(c)}” removida${c.itemCount ? ` (com ${c.itemCount} ${c.itemCount === 1 ? "item" : "itens"})` : ""}` };
      if (c.op === "renamed") return { sign: "~", text: `Série “${c.from}” renomeada para “${historySeriesLabel(c)}”` };
      return { sign: "~", text: "Ordem das séries alterada" };
    case "book":
      return c.op === "added" ? { sign: "+", text: `Livro ${t}` } : { sign: "−", text: `Livro ${t}` };
    case "optional":
      return c.op === "added" ? { sign: "~", text: `${t} marcado como opcional` } : { sign: "~", text: `${t} voltou a ser obrigatório` };
    case "didatico":
      return c.op === "added" ? { sign: "+", text: `Didático ${t}` } : { sign: "−", text: `Didático ${t}` };
    case "papelaria":
      if (c.op === "added") return { sign: "+", text: `Papelaria ${t} (${fmtPap(c.to)})` };
      if (c.op === "removed") return { sign: "−", text: `Papelaria ${t}` };
      return { sign: "~", text: `Papelaria ${t}: ${fmtPap(c.from)} → ${fmtPap(c.to)}` };
    case "material":
      if (c.op === "added") return { sign: "+", text: `Material extra ${t}` };
      if (c.op === "removed") return { sign: "−", text: `Material extra ${t}` };
      return { sign: "~", text: `Material extra ${t} editado` };
    case "field": {
      const label = HISTORY_FIELD_LABELS[c.field] || c.field;
      const from = fmtValue(c.from);
      const to = fmtValue(c.to);
      return { sign: "~", text: from != null && to != null ? `${label}: ${from} → ${to}` : `${label} alterado` };
    }
    default:
      return { sign: "~", text: "Alteração" };
  }
}

function versionTitle(v) {
  if (!v) return "";
  if (v.kind === "manual") return v.label || "Versão sem nome";
  if (v.kind === "auto") return `Automática — início de ${fmtDay(v.createdAt)}`;
  if (v.kind === "backup") {
    const from = v.restoredFrom ? (v.restoredFrom.label ? `“${v.restoredFrom.label}”` : "outra versão") : "outra versão";
    return `Cópia de segurança antes de restaurar ${from}`;
  }
  return v.label || "Versão";
}

function summaryText(s) {
  if (!s) return "";
  const n = (count, one, many) => `${count} ${count === 1 ? one : many}`;
  const parts = [];
  if (s.series) parts.push(n(s.series, "série", "séries"));
  parts.push(n(s.books || 0, "livro", "livros"));
  if (s.didaticos) parts.push(n(s.didaticos, "didático", "didáticos"));
  if (s.papelaria) parts.push(n(s.papelaria, "item de papelaria", "itens de papelaria"));
  if (s.materials) parts.push(n(s.materials, "material extra", "materiais extras"));
  return parts.join(" · ");
}

const SIGN_COLORS = { "+": "#2f8a5b", "−": "#b53a3a", "~": "var(--teal-dark)" };

const panelBtn = (variant) => ({
  padding: "7px 12px", borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
  border: variant === "primary" ? "none" : "1px solid var(--border)",
  background: variant === "primary" ? "var(--teal)" : variant === "danger" ? "#e0798e" : "#fff",
  color: variant === "primary" || variant === "danger" ? "#fff" : "var(--text)",
  whiteSpace: "nowrap",
});

function HistoryEntry({ entry, versionsById }) {
  const [expanded, setExpanded] = useState(false);
  const changes = entry.changes || [];
  const COLLAPSED = 8;
  const shown = expanded ? changes : changes.slice(0, COLLAPSED);
  const hidden = (entry.changeCount || changes.length) - shown.length;

  let title;
  if (entry.kind === "created") title = "Lista criada";
  else if (entry.kind === "version") title = `Salvou a versão “${entry.versionLabel || "sem nome"}”`;
  else if (entry.kind === "restore") {
    const v = versionsById[entry.versionId];
    const name = v ? versionTitle(v) : (entry.versionLabel ? `“${entry.versionLabel}”` : "uma versão anterior");
    title = `Restaurou ${v && v.kind === "manual" ? `“${name}”` : name}`;
  } else {
    title = `${entry.changeCount || changes.length} ${(entry.changeCount || changes.length) === 1 ? "alteração" : "alterações"}`;
  }

  const sameMinute = fmtTime(entry.startedAt) === fmtTime(entry.endedAt) && fmtDay(entry.startedAt) === fmtDay(entry.endedAt);
  const when = sameMinute ? fmtDateTime(entry.endedAt) : `${fmtDateTime(entry.startedAt)} – ${fmtTime(entry.endedAt)}`;

  return (
    <div style={{ padding: "12px 14px", border: "1px solid var(--border)", borderRadius: 12, background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--text)" }}>{title}</div>
        <div style={{ fontSize: 11.5, color: "var(--text-mute)" }}>{when}</div>
      </div>
      <div style={{ fontSize: 12, color: "var(--text-mute)", marginTop: 2 }}>{actorLabel(entry.actor)}</div>
      {/* Ordem cronológica, cada linha com horário e série — é log de
          auditoria (achar quem fez uma alteração errada e quando).
          Registros antigos (antes de 02/10/2026) não têm `at`. */}
      {shown.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
          {shown.map((c, i) => {
            const d = describeChange(c);
            const series = c.type === "series" ? null : historySeriesLabel(c);
            return (
              <div key={i} style={{ display: "flex", gap: 6, fontSize: 12.5, color: "var(--text)", lineHeight: 1.45 }}>
                {c.at && <span style={{ flexShrink: 0, color: "var(--text-mute)", fontVariantNumeric: "tabular-nums" }}>{fmtTime(c.at)}</span>}
                <span style={{ width: 10, flexShrink: 0, fontWeight: 700, color: SIGN_COLORS[d.sign] }}>{d.sign}</span>
                <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                  {series && <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-mute)", marginRight: 6 }}>{series}</span>}
                  {d.text}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {hidden > 0 && (
        <button onClick={() => setExpanded(!expanded)} style={{ marginTop: 6, background: "transparent", border: "none", padding: 0, color: "var(--teal-dark)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          {expanded ? "Mostrar menos" : `Ver mais ${hidden}`}
        </button>
      )}
      {expanded && entry.truncated > 0 && (
        <div style={{ marginTop: 4, fontSize: 11.5, color: "var(--text-mute)" }}>+ {entry.truncated} alterações não detalhadas</div>
      )}
    </div>
  );
}

function VersionRow({ version, onRestore, onDelete, busy }) {
  const [confirming, setConfirming] = useState(null); // "restore" | "delete" | null
  return (
    <div style={{ padding: "12px 14px", border: "1px solid var(--border)", borderRadius: 12, background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--text)", overflowWrap: "anywhere" }}>{versionTitle(version)}</div>
          <div style={{ fontSize: 12, color: "var(--text-mute)", marginTop: 2 }}>
            {fmtDateTime(version.createdAt)}{version.createdBy ? ` · ${actorLabel(version.createdBy)}` : ""}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-mute)", marginTop: 2 }}>{summaryText(version.summary)}</div>
        </div>
        {!confirming && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button disabled={busy} onClick={() => setConfirming("restore")} style={panelBtn()}>Restaurar</button>
            {version.kind === "manual" && (
              <button disabled={busy} onClick={() => setConfirming("delete")} title="Excluir versão" aria-label="Excluir versão" style={panelBtn()}>✕</button>
            )}
          </div>
        )}
      </div>
      {confirming && (
        <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10, background: confirming === "delete" ? "#fdecec" : "var(--bg-light)", border: `1px solid ${confirming === "delete" ? "#e0798e" : "var(--border)"}` }}>
          <div style={{ fontSize: 12.5, color: "var(--text)", marginBottom: 8 }}>
            {confirming === "restore"
              ? "A lista atual vai ser substituída por esta versão para todas as contas da escola. Antes disso, guardamos uma cópia de segurança da lista atual — dá pra desfazer restaurando essa cópia."
              : "Excluir esta versão? Isso não pode ser desfeito."}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button disabled={busy} style={panelBtn(confirming === "delete" ? "danger" : "primary")}
              onClick={async () => { const ok = await (confirming === "restore" ? onRestore(version) : onDelete(version)); if (ok !== false) setConfirming(null); }}>
              {busy ? "Aguarde…" : confirming === "restore" ? "Restaurar esta versão" : "Excluir"}
            </button>
            <button disabled={busy} onClick={() => setConfirming(null)} style={panelBtn()}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryPanel({ onClose }) {
  const ctx = useApp();
  const account = ctx && ctx.account;
  const uid = account && account.user && account.user.id;
  const schoolId = (account && account.school && account.school.id) || null;

  const [tab, setTab] = useState("changes");
  const [entries, setEntries] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const [notice, setNotice] = useState(null);
  const PAGE = 30;

  const versionsById = useMemo(() => {
    const m = {};
    versions.forEach((v) => { m[v.id] = v; });
    return m;
  }, [versions]);

  const reload = async () => {
    if (!uid) return;
    setLoading(true);
    setError(null);
    try {
      // Garante que a última edição desta aba já chegou no backend antes de
      // ler o log (senão a mudança que a pessoa acabou de fazer não aparece).
      if (ctx.flushWizardSave) await ctx.flushWizardSave();
      const [h, v] = await Promise.all([
        window.ScolistAPI.listWizardHistory(uid, schoolId, { limit: PAGE }),
        window.ScolistAPI.listWizardVersions(uid, schoolId),
      ]);
      setEntries(h);
      setHasMore(h.length === PAGE);
      setVersions(v);
    } catch (e) {
      setError((e && e.message) || "Não foi possível carregar o histórico.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [uid, schoolId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const loadMore = async () => {
    const last = entries[entries.length - 1];
    if (!last) return;
    setBusy(true);
    try {
      const more = await window.ScolistAPI.listWizardHistory(uid, schoolId, { limit: PAGE, before: last.endedAt });
      setEntries(entries.concat(more));
      setHasMore(more.length === PAGE);
    } catch (e) {
      setError((e && e.message) || "Não foi possível carregar mais.");
    } finally {
      setBusy(false);
    }
  };

  const saveVersion = async () => {
    const name = label.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      if (ctx.flushWizardSave) await ctx.flushWizardSave();
      await window.ScolistAPI.saveWizardVersion(uid, schoolId, name);
      setLabel("");
      setNotice(`Versão “${name}” salva.`);
      await reload();
    } catch (e) {
      setError((e && e.message) || "Não foi possível salvar a versão.");
    } finally {
      setBusy(false);
    }
  };

  const restore = async (version) => {
    setBusy(true);
    setError(null);
    try {
      if (ctx.flushWizardSave) await ctx.flushWizardSave();
      const progress = await window.ScolistAPI.restoreWizardVersion(uid, schoolId, version.id, new Date().toISOString());
      if (ctx.applyRestoredProgress) ctx.applyRestoredProgress(progress);
      setNotice(`Lista restaurada para “${versionTitle(version)}”.`);
      setTab("changes");
      await reload();
      return true;
    } catch (e) {
      setError((e && e.message) || "Não foi possível restaurar a versão.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const remove = async (version) => {
    setBusy(true);
    setError(null);
    try {
      await window.ScolistAPI.deleteWizardVersion(uid, schoolId, version.id);
      setVersions(versions.filter((v) => v.id !== version.id));
      return true;
    } catch (e) {
      setError((e && e.message) || "Não foi possível excluir a versão.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const tabBtn = (id, text) => (
    <button onClick={() => setTab(id)} style={{
      flex: 1, padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer",
      fontSize: 13, fontWeight: 700,
      background: tab === id ? "#fff" : "transparent",
      color: tab === id ? "var(--text)" : "var(--text-mute)",
      boxShadow: tab === id ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
    }}>{text}</button>
  );

  return (
    // zIndex acima do LangToggle (9999, app.jsx) — senão ele fica por cima
    // do ✕ de fechar, no mesmo canto superior direito.
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(18,51,58,.45)", display: "flex", justifyContent: "flex-end" }}>
      <div role="dialog" aria-modal="true" aria-label="Histórico da lista" onClick={(e) => e.stopPropagation()} style={{
        width: "min(560px, 100vw)", height: "100%", background: "var(--bg-light)",
        display: "flex", flexDirection: "column", boxShadow: "-8px 0 30px rgba(0,0,0,0.15)",
      }}>
        <div style={{ padding: "18px 20px 12px", borderBottom: "1px solid var(--border)", background: "#fff" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: "var(--text)" }}>Histórico da lista</div>
              <div style={{ fontSize: 12, color: "var(--text-mute)" }}>Alterações de todas as contas da escola e versões salvas</div>
            </div>
            <button onClick={onClose} aria-label="Fechar" style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid var(--border)", background: "#fff", cursor: "pointer", fontSize: 14, color: "var(--text-mute)" }}>✕</button>
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 12, padding: 4, borderRadius: 10, background: "var(--bg-light)" }}>
            {tabBtn("changes", "Alterações")}
            {tabBtn("versions", `Versões${versions.length ? ` (${versions.length})` : ""}`)}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "14px 20px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
          {notice && (
            <div style={{ padding: "9px 12px", borderRadius: 10, background: "#e6f5ee", border: "1px solid #9fd3b8", fontSize: 12.5, color: "#1f6b45", display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span>{notice}</span>
              <button onClick={() => setNotice(null)} aria-label="Fechar aviso" style={{ background: "transparent", border: "none", cursor: "pointer", color: "inherit" }}>✕</button>
            </div>
          )}
          {error && (
            <div style={{ padding: "9px 12px", borderRadius: 10, background: "#fdecec", border: "1px solid #e0798e", fontSize: 12.5, color: "#b53a3a" }}>{error}</div>
          )}

          {tab === "versions" && (
            <div style={{ padding: "12px 14px", border: "1px solid var(--border)", borderRadius: 12, background: "#fff" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 8 }}>Salvar a lista atual como versão</div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveVersion(); }}
                  maxLength={120}
                  placeholder="Ex.: Enviada para aprovação da direção"
                  style={{ flex: 1, minWidth: 0, padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border)", fontSize: 13 }}
                />
                <button disabled={busy || !label.trim()} onClick={saveVersion} style={Object.assign(panelBtn("primary"), { opacity: busy || !label.trim() ? 0.5 : 1 })}>Salvar versão</button>
              </div>
              <div style={{ fontSize: 11.5, color: "var(--text-mute)", marginTop: 6 }}>
                Além das versões que você salvar, guardamos uma cópia automática no início de cada dia em que a lista é editada.
              </div>
            </div>
          )}

          {loading ? (
            <div style={{ fontSize: 13, color: "var(--text-mute)", padding: 12 }}>Carregando…</div>
          ) : tab === "changes" ? (
            entries.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--text-mute)", padding: 12 }}>Nenhuma alteração registrada ainda. As próximas edições da lista vão aparecer aqui.</div>
            ) : (
              <React.Fragment>
                {entries.map((e) => <HistoryEntry key={e.id} entry={e} versionsById={versionsById}/>)}
                {hasMore && <button disabled={busy} onClick={loadMore} style={Object.assign(panelBtn(), { alignSelf: "center" })}>{busy ? "Carregando…" : "Carregar mais"}</button>}
              </React.Fragment>
            )
          ) : (
            versions.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--text-mute)", padding: 12 }}>Nenhuma versão salva ainda.</div>
            ) : (
              versions.map((v) => <VersionRow key={v.id} version={v} onRestore={restore} onDelete={remove} busy={busy}/>)
            )
          )}
        </div>
      </div>
    </div>
  );
}

window.HistoryPanel = HistoryPanel;
